import React, { useEffect, useMemo, useState } from "react";
import {
    Header,
    Checkbox,
    Modal,
    Card,
} from "semantic-ui-react";
import {
    AppTable,
    FlexBox,
    FlexRow,
    FlexRowBetween,
    LoaderBar,
} from "../../components";
import { Key, PencilSimple, X } from "phosphor-react";
import { useTabletAndBelowMediaQuery } from "layout";
import { AppButton, AppPrimaryButton } from "elements";
import { GetMenuList, GetMenuPermissionList } from "hooks/menu-setting";
import { DateFormat, DateUtility } from "utility";
import ActionBoxes from "components/ActionBoxes";
import { GetPermissionList } from "hooks/permission";
import PermissionModal from "screens/permissions/PermissionModal";
import MenuModal from "./MenuModal";
import { MenuSettingService } from "utility/services/menu-setting";
import styled from "styled-components";

const GroupCardMetaRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
    font-size: 13px;

    .label {
        font-weight: 600;
        color: #334155;
        min-width: 100px;
        flex-shrink: 0;
    }

    .value {
        color: #0f172a;
        font-weight: 500;
    }
`;

const MenuCard = ({ item, handleEdit, setIsPermissionsModalOpen, setSelectedMenu }) => (
    <Card fluid className="mb-3">
        <Card.Content>
            <Card.Header style={{ fontWeight: 600, color: '#0f172a' }}>
                {item.Title}
            </Card.Header>
            <Card.Description className="mt-2">
                <GroupCardMetaRow>
                    <span className="label">Menu ID</span>
                    <span className="value">{item.MenuId}</span>
                </GroupCardMetaRow>
                <GroupCardMetaRow>
                    <span className="label">Route</span>
                    <span className="value">{item.Route || '(none)'}</span>
                </GroupCardMetaRow>
                <GroupCardMetaRow>
                    <span className="label">Menu Key</span>
                    <span className="value">{item.MenuKey}</span>
                </GroupCardMetaRow>
                <GroupCardMetaRow>
                    <span className="label">Created On</span>
                    <span className="value">
                        {item.CreatedOn &&
                            DateUtility.dateToLocalISO(
                                new Date(item.CreatedOn),
                                DateFormat.date,
                            )}
                    </span>
                </GroupCardMetaRow>
                <FlexBox className="justify-content-end gap-2 mt-3">
                    <ActionBoxes
                        item={item}
                        menuItems={[
                            {
                                label: "Edit",
                                icon: PencilSimple,
                                onClick: () => handleEdit(item),
                            },
                            {
                                label: "Permissions",
                                icon: Key,
                                onClick: () => {
                                    setIsPermissionsModalOpen(true);
                                    setSelectedMenu(item);
                                },
                            },
                        ]}
                    />
                </FlexBox>
            </Card.Description>
        </Card.Content>
    </Card>
);

const MenuPermissionCard = ({ item, permissionData, handleTogglePermission }) => {
    const isAssigned = permissionData?.some(
        p => p.PermissionId === item.PermissionId,
    );
    return (
        <Card fluid className="mb-2">
            <Card.Content className="p-3">
                <FlexRowBetween>
                    <div>
                        <div style={{ fontWeight: 600, color: '#0f172a' }}>{item.PermissionName}</div>
                        <div className="text-muted text-xs">{item.PermissionKey}</div>
                    </div>
                    <Checkbox
                        checked={isAssigned}
                        onChange={(e, data) => handleTogglePermission(item, data.checked)}
                    />
                </FlexRowBetween>
            </Card.Content>
        </Card>
    );
};

export const MenuSettingTab = () => {
    const { data, refresh, options, loading } = GetMenuList();
    const [editItem, setEditItem] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false);
    const [selectedMenu, setSelectedMenu] = useState(null);
    const {
        data: permissionData,
        setData: setPermissionData,
        loading: permissionLoading,
        refresh: permissionRefresh,
    } = GetMenuPermissionList(selectedMenu?.MenuId);
    const [isPermissionCreateModalOpen, setIsPermissionCreateModalOpen] = useState(false);
    const { data: allPermissions, refresh: permissionListRefresh } = GetPermissionList();

    const isMobile = useTabletAndBelowMediaQuery();

    useEffect(() => {
        if (selectedMenu?.MenuId) {
            permissionRefresh();
        }
    }, [selectedMenu]);

    const handleEdit = (item) => {
        setEditItem(item);
        setIsModalOpen(true);
    };

    const handleAdd = () => {
        setEditItem(null);
        setIsModalOpen(true);
    };

    const handleTogglePermission = async (permission, isChecked) => {
        if (!selectedMenu?.MenuId) return;

        // Optimistically update the state immediately
        setPermissionData(prev => {
            if (isChecked) {
                if (!prev.some(p => p.PermissionId === permission.PermissionId)) {
                    return [...prev, permission];
                }
                return prev;
            }
            return prev.filter(p => p.PermissionId !== permission.PermissionId);
        });

        try {
            if (isChecked) {
                await MenuSettingService.menuPermissionAssign(
                    selectedMenu.MenuId,
                    permission.PermissionId,
                );
            } else {
                await MenuSettingService.menuPermissionRemove(
                    selectedMenu.MenuId,
                    permission.PermissionId,
                );
            }
            permissionRefresh(false);
        } catch (error) {
            console.log(error);
            // Revert state update by refreshing with a loader to ensure UI sync
            permissionRefresh(true);
        }
    };

    const columns = useMemo(
        () => [
            {
                title: "Menu Id",
                key: "MenuId",
            },
            {
                title: "Menu Title",
                key: "Title",
            },
            {
                title: "Route",
                key: "Route",
            },
            {
                title: "Menu Key",
                key: "MenuKey",
            },
            {
                title: "Created On",
                render: (item) => (
                    <span>
                        {DateUtility.dateToLocalISO(
                            new Date(item.CreatedOn),
                            DateFormat.date,
                        )}
                    </span>
                ),
            },
            {
                title: "Actions",
                render: (item) => (
                    <FlexBox className="justify-content-center">
                        <ActionBoxes
                            item={item}
                            menuItems={[
                                {
                                    label: "Edit",
                                    icon: PencilSimple,
                                    onClick: () => handleEdit(item),
                                },
                                {
                                    label: "Permissions",
                                    icon: Key,
                                    onClick: () => {
                                        setIsPermissionsModalOpen(true);
                                        setSelectedMenu(item);
                                    },
                                },
                            ]}
                        />
                    </FlexBox>
                ),
            },
        ],
        [data, isMobile],
    );

    const permissionColumns = useMemo(
        () => [
            {
                title: "Assign",
                render: (item) => {
                    const isAssigned = permissionData?.some(
                        p => p.PermissionId === item.PermissionId,
                    );
                    return (
                        <Checkbox
                            checked={isAssigned}
                            onChange={(e, data) => handleTogglePermission(item, data.checked)}
                        />
                    );
                },
            },
            {
                title: "Permission Name",
                key: "PermissionName",
            },
            {
                title: "Permission Key",
                key: "PermissionKey",
            },
        ],
        [permissionData, allPermissions, isMobile],
    );

    const handleClosePermissions = () => {
        setIsPermissionsModalOpen(false);
        setSelectedMenu(null);
    };

    return (
        <>
            <Header as="h3">Configured Menus</Header>
            <FlexRow className="justify-content-end mb-3">
                <AppPrimaryButton onClick={handleAdd}>
                    Add Menu
                </AppPrimaryButton>
            </FlexRow>

            {loading ? <LoaderBar /> : <AppTable
                data={data?.length > 0 ? data : []}
                columns={columns}
                pageChanged={() => { }}
                filter=""
                totalCount={0}
                hidePagination="true"
                mobileCard={
                    <MenuCard
                        handleEdit={handleEdit}
                        setIsPermissionsModalOpen={setIsPermissionsModalOpen}
                        setSelectedMenu={setSelectedMenu}
                    />
                }
            />}

            <Modal open={isPermissionsModalOpen} onClose={handleClosePermissions} size="large">
                <Modal.Header>
                    <FlexRowBetween>
                        <div>
                            <h3 style={{ margin: 0 }}>{selectedMenu?.Title} Menu Permissions</h3>
                        </div>
                        <X
                            className="pointer"
                            size={28}
                            onClick={handleClosePermissions}
                            style={{ cursor: 'pointer' }}
                        />
                    </FlexRowBetween>
                </Modal.Header>
                <Modal.Content scrolling style={{ maxHeight: "70vh", overflowY: "auto" }}>
                    <FlexRow className="justify-content-end mb-4">
                        <AppButton
                            style={{ width: "200px" }}
                            type="button"
                            onClick={() => setIsPermissionCreateModalOpen(true)}
                        >
                            Add Permission
                        </AppButton>
                    </FlexRow>
                    {permissionLoading ? <LoaderBar /> : <AppTable
                        data={allPermissions?.length > 0 ? allPermissions : []}
                        columns={permissionColumns}
                        pageChanged={() => { }}
                        filter=""
                        totalCount={0}
                        hidePagination="true"
                        mobileCard={
                            <MenuPermissionCard
                                permissionData={permissionData}
                                handleTogglePermission={handleTogglePermission}
                            />
                        }
                    />}
                </Modal.Content>
                <Modal.Actions>
                    <AppPrimaryButton onClick={handleClosePermissions}>Close</AppPrimaryButton>
                </Modal.Actions>
            </Modal>
            <PermissionModal
                open={isPermissionCreateModalOpen}
                onClose={() => setIsPermissionCreateModalOpen(false)}
                editItem={null}
                refresh={permissionListRefresh}
            />
            <MenuModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                editItem={editItem}
                refresh={refresh}
                options={options}
                data={data}
            />
        </>
    );
};